<template>
  <div id="group2">
    <h4>Select Products for Client</h4>
    <p>
      Find any product on RegimenPro<sup>®</sup> and add it to your client's order
    </p>

    <div class="wrap-table border-table pos-rel regimen-list">
      <div v-if="initialPhysicianCount === 1">
        <p class="text-sm text-gray-600">
          Physician: {{ physicians[0].name }}
        </p>
      </div>

      <div v-else-if="initialPhysicianCount > 1 && initialPhysicianCount <= 3">
        <div class="col-sm-12 mb-2"><strong> Select Skincare Pro </strong></div>
        <label
          v-for="physician in physicians"
          :key="physician.id"
          class="col-sm-12"
        >
          <input
            type="radio"
            name="physician"
            :value="physician.id"
            v-model="selectedPhysicianId"
            @change="onSelectionChange"
          />
          <span class="ml-2">{{ physician.name }}</span>
        </label>
      </div>

      <div v-else-if="initialPhysicianCount > 3">
        <strong class="mr-2"> Select Skincare Pro: </strong>
        <select-physican
          v-model="selectedPhysicianId"
          title="All Skincare Pros"
          :items="valuesForSelect(physicians)"
          :total="physiciansTotal"
          :loading="isPhysiciansLoading"
          :name="`physician-select`"
          :filters="filters"
          @load-more="loadMorePhysiciansWrapper"
          @input="onSelectionChange"
        />
      </div>
      <div v-else >
        <span>Physicians Loading.........</span>
      </div>

    </div>

    <div class="wrap-table border-table pos-rel regimen-list">
      <div class="top-bar">
        <div class="row row-group align-items-center">
          <div class="col-12">
            <h4>Add Products From Client's Regimen</h4>
          </div>
        </div>
      </div>
      <div class="products">
        <div
          v-for="product in regimenProducts"
          :key="product.id"
          class="row row-group btn-sm-actions"
        >
          <div class="col-sm-2">
            <div class="md-image">
              <img
                v-if="product.image"
                :src="product.image"
                alt="img"
              >
            </div>
          </div>
          <div class="col-sm-7">
            <div class="row-large">
              <div class="col-lg-5">
                <strong v-html="product.title" />
              </div>
              <div class="col-lg-4 vendor">
                {{ product.vendor }}
              </div>
              <div class="col-lg-3">
                ${{ product.price }}
              </div>
            </div>
          </div>
          <div class="col-sm-3 pr-0 text-right btn-wrap">
            <div
              v-if="!isActiveSubscription(product.subscription)"
            >
              <a
                class="btn btn-primary btn-view hover-btn"
                @click.prevent="$emit('edit-product-modal', product)"
              >
                View
              </a>
              <a
                v-if="!isVariantSelected(product.variant_graph_id)"
                class="btn btn-primary hover-btn"
                @click.prevent="$emit('product-selected', product)"
              >
                Add
              </a>
              <span
                v-if="isVariantSelected(product.variant_graph_id)"
                class="product-added"
              >
                Added
              </span>
            </div>
            <a
              v-else
              class="btn btn-subscription btn-primary btn-view hover-btn"
              @click.prevent="$emit('edit-product-modal', product)"
            >
              Edit Subscription
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .vendor {
    word-break: break-all;
  }
  .btn-wrap {
    min-width: 100px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .btn-view {
    background: transparent;
    color: #a989de !important;
    &:hover {
      color: #fff !important;
    }
  }
  .btn-primary {
    margin-right: 0 !important;
  }
</style>

<script>
import { formatProduct, formatStorefrontMoney } from '@/helper';
import physician from '@/mixins/physician';
import SelectPhysican from '@/views/components/Select';
import productList from '@/mixins/productList';
import { ProductService } from '@/services/api.service';
// import productList from '@/mixins/productListWithAlgoliaSearch';

// @group Partials
export default {
  name: 'CreateOrderProducts',
  components: {
    SelectPhysican
  },

  mixins: [physician, productList],

  props: {
    regimenProducts: {
      type: Array,
      default() {
        return [];
      }
    },
    allowFilterByPractice: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      initialPhysicianCount: 0,
      selectedPhysicianId: null
    }
  },

  watch: {
    physicians(newVal) {
      if (newVal?.length === 1) {
        this.selectedPhysicianId = newVal[0].id;
        this.onSelectionChange();
      }
    }
  },

  created() {
    this.loadOnInit = false;
  },

  mounted() {
    this.loadMorePhysicians({
      type: "search",
      filters: { practice_id: this.patient.practice.id }
    }).then(() => {
      this.initialPhysicianCount = this.physicians.length;

      if (this.initialPhysicianCount === 1) {
        this.selectedPhysicianId = this.physicians[0].id;
        this.$emit("physician-selected", this.selectedPhysicianId);
      }else {
        this.selectedPhysicianId = this.patient.effective_sp_id;
        this.$emit("physician-selected", this.selectedPhysicianId);
      }
    });
  },

  methods: {
    formatStorefrontMoney: formatStorefrontMoney,
    // @vuese
    // Used to add all product except subscriptions
    addAllProducts() {
      this.regimenProducts.forEach(product => {
        // Do not add product with subscription
        if (product.subscription) return;
        this.$emit('product-selected', product);
      });
    },

    // @vuese
    // Used to pass selected product to parent
    // @arg Product object
    productSelected(product) {
      const newVariant = product.variants.find(variant =>
        !this.selectedProducts.find(selectedProduct => selectedProduct.variant_graph_id === variant.id)
      );
      this.getSellingPlan(product);
      this.$emit('product-selected', formatProduct(product, newVariant));
    },

    getSellingPlan(product) {
      ProductService.getSellingPlanByVariant(product.handle)
        .then(resp => {
          if(resp.data){
            if (
              resp.data.data &&
              resp.data.data.product &&
              resp.data.data.product.sellingPlanGroups &&
              resp.data.data.product.sellingPlanGroups.edges.length > 0 &&
              resp.data.data.product.sellingPlanGroups.edges[0].node.sellingPlans
            ) {
              product.sellingPlans = resp.data.data.product.sellingPlanGroups.edges[0].node.sellingPlans.edges.map(plan => ({
                value: plan.node.id,
                text: plan.node.name
              }));
            }
          }
        })
        .finally(() => {
          this.sending = false;
        });
    },

    // @vuese
    // Used to check if product selected
    selected(productId) {
      return this.selectedProducts.find(product => {
        return product.shopify_product_graph_id == productId;
      }) || this.isActiveSubscriptionInRegimen(productId);
    },

    // @vuese
    // Used to check if product is active subscripbtion in regimen
    // @arg Product ID
    isActiveSubscriptionInRegimen(productId) {
      const product = this.findInRegimen(productId);
      return product && product.subscription ? this.isActiveSubscription(product.subscription) : false;
    },

    // @vuese
    // Used to find product in regimen
    // @arg Product ID
    findInRegimen(productId) {
      return this.regimenProducts.find(product => {
        return product.shopify_product_graph_id == productId;
      });
    },

    // @vuese
    // Used to check if subscription is active
    // @arg Subscription
    isActiveSubscription(subscription) {
      return subscription && subscription.status == 'active';
    },

    loadMorePhysiciansWrapper(obj) {
      this.loadMorePhysicians({ ...obj, filters: {practice_id: this.patient.practice.id} });
    },

    onSelectionChange() {
      this.$emit('physician-selected', this.selectedPhysicianId);
    }
  }
};
</script>